import React from 'react'
import { AiOutlineMail } from '@react-icons/all-files/ai/AiOutlineMail'
import { AiOutlinePhone } from '@react-icons/all-files/ai/AiOutlinePhone'
import { AiOutlineSearch } from '@react-icons/all-files/ai/AiOutlineSearch'
import { FaRegCalendarCheck } from '@react-icons/all-files/fa/FaRegCalendarCheck'
import { useDispatch, useSelector } from 'react-redux'

import productionHero from '../public/Pictures/miami-av-production-team-corporate-event-hero.jpg'
import * as gtag from '../lib/gtag'
import { getWorkingOrder } from '../store/slices/entities/orders'
import { SHOW_POPUP } from '../store/slices/ui'
import HomepageHero from './design-system/marketing/HomepageHero'
import { useOrderFlow } from './unified-order/OrderFlowProvider'
import ProductionPopup from './DeferredProductionPopup'
import QuotePop from './DeferredQuotePopup'

const HomepageMainHero = ({ heroContent, reviewData, totalEvents }) => {
  const dispatch = useDispatch()
  const order = useSelector(getWorkingOrder)
  const { launchIntent } = useOrderFlow()

  const launchEventBuilder = sourceSurface => launchIntent({
    sourceSurface,
    fulfillment: 'delivery_setup'
  }, () => dispatch(SHOW_POPUP({
    size: 'xl',
    key: 'prodpop',
    body: <ProductionPopup />
  })))

  const launchProductionQuote = sourceSurface => launchIntent({
    sourceSurface,
    fulfillment: 'delivery_setup'
  }, () => dispatch(SHOW_POPUP({
    key: 'quote',
    size: 'medium',
    body: <QuotePop />
  })))

  const trackHeroAction = (cta, fallbackEventName, value) => gtag.event({
    action: cta.eventName || fallbackEventName,
    label: 'leads',
    value,
    category: 'orders'
  })

  const actions = (heroContent?.ctas || [])
    .filter(cta => !(cta.url === '#event-builder' && order?._id))
    .map(cta => {
      if (cta.url === '#request-production-quote') {
        return {
          ...cta,
          icon: AiOutlineMail,
          onClick: () => {
            trackHeroAction(cta, 'home_quote_click_CTA', 3)
            launchProductionQuote('homepage_hero_quote')
          }
        }
      }

      if (cta.url === '#event-builder') {
        return {
          ...cta,
          icon: FaRegCalendarCheck,
          onClick: () => {
            trackHeroAction(cta, 'home_package_builder_click_CTA', 3)
            launchEventBuilder('homepage_hero_event_builder')
          }
        }
      }

      if (cta.url?.startsWith('tel:')) {
        return {
          ...cta,
          external: true,
          href: cta.url,
          icon: AiOutlinePhone,
          onClick: () => trackHeroAction(cta, 'home_phone_call_CTA', 15)
        }
      }

      return {
        ...cta,
        href: cta.url,
        icon: AiOutlineSearch,
        onClick: () => trackHeroAction(cta, 'home_catalog_click_CTA', 15)
      }
    })
    .map(action => ({
      ...action,
      dataConversionKey: action.eventName || `homepage.hero.${action.style || 'action'}.${String(action.label || 'cta').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      dataConversionLocation: 'hero',
      dataConversionChannel: action.href?.startsWith('tel:')
        ? 'phone'
        : action.url === '#request-production-quote'
          ? 'quote'
          : action.url === '#event-builder'
            ? 'builder'
            : 'rental',
      dataConversionSalesPath: action.href?.startsWith('tel:') || action.url === '#request-production-quote'
        ? 'managed'
        : 'self_service'
    }))

  const primaryActions = actions.filter(action => ['primary', 'rental'].includes(action.style))
  const contactAction = actions.find(action => action.style === 'contact')
  const secondaryActions = actions.filter(action => action.style === 'pricing')

  return (
    <HomepageHero
      actions={primaryActions}
      contactAction={contactAction}
      content={heroContent}
      image={productionHero}
      reviewData={reviewData}
      secondaryActions={secondaryActions}
      totalEvents={totalEvents}
    />
  )
}

export default HomepageMainHero
